/**
 * ChatThinkingManager - Утилита для управления думающими сообщениями в чате
 *
 * Обеспечивает централизованное управление временными сообщениями "думает"
 * с автоматической очисткой и обработкой ошибок
 */

import { THINKING_CONFIG } from '@/config/thinkingConfig';

class ChatThinkingManager {
  constructor() {
    this.activeThinkingMessages = new Map(); // messageId -> { chatId, timestamp, timeoutId }
    this.cleanupInterval = null;
    this.THINKING_TIMEOUT = THINKING_CONFIG.DEFAULT_TIMEOUT; // Используем конфигурацию

    this.startCleanupInterval();
  }

  /**
   * Добавляет думающее сообщение
   * @param {string} messageId - Уникальный ID сообщения
   * @param {number} chatId - ID чата
   * @param {Function} onTimeout - Callback для таймаута
   * @param {Function} onExtend - Callback для продления времени
   */
  addThinkingMessage(messageId, chatId, onTimeout, onExtend = null) {
    console.log('🤔 Adding thinking message:', { messageId, chatId });

    // Очищаем предыдущее сообщение если есть (только таймер, не удаляем)
    const existing = this.activeThinkingMessages.get(messageId);
    if (existing) {
      console.log('🕒 Clearing existing timeout for:', messageId);
      clearTimeout(existing.timeoutId);
      if (existing.extendTimeoutId) {
        clearTimeout(existing.extendTimeoutId);
      }
    }

    // Создаем таймер для автоматической обработки таймаута
    const timeoutId = setTimeout(() => {
      console.warn('⏰ Thinking message timeout:', messageId);
      // Вместо немедленного вызова onTimeout, пытаемся продлить время
      this.handleTimeoutWithExtension(messageId, chatId, onTimeout, onExtend);
    }, this.THINKING_TIMEOUT);

    // Создаем таймер для автоматического продления (за 2 минуты до истечения)
    const extendTimeoutId = setTimeout(() => {
      console.log('⏰ Auto-extending thinking time for:', messageId);
      this.autoExtendThinkingTime(messageId, onExtend);
    }, this.THINKING_TIMEOUT - THINKING_CONFIG.AUTO_EXTEND_THRESHOLD);

    this.activeThinkingMessages.set(messageId, {
      chatId,
      timestamp: Date.now(),
      timeoutId,
      extendTimeoutId,
      onTimeout, // Сохраняем callback для возможности продления
      onExtend, // Callback для продления
      extensionCount: 0, // Счетчик продлений
    });
  }

  /**
   * Заменяет думающее сообщение на реальное
   * @param {string} oldMessageId - ID думающего сообщения
   * @param {string} newMessageId - ID нового сообщения
   */
  replaceThinkingMessage(oldMessageId, newMessageId) {
    console.log('✅ Replacing thinking message:', { oldMessageId, newMessageId });

    const thinking = this.activeThinkingMessages.get(oldMessageId);
    if (thinking) {
      console.log('🕒 Clearing timeout for replaced thinking message:', oldMessageId);

      // Очищаем все таймауты
      clearTimeout(thinking.timeoutId);
      if (thinking.extendTimeoutId) {
        clearTimeout(thinking.extendTimeoutId);
      }

      // Удаляем из активных сообщений
      this.activeThinkingMessages.delete(oldMessageId);

      console.log('✅ Successfully cleaned up thinking message:', oldMessageId);
    } else {
      console.warn('⚠️ Thinking message not found in manager:', oldMessageId);
      console.log('🔍 Available thinking messages:', Array.from(this.activeThinkingMessages.keys()));
    }
  }

  /**
   * Проверяет, является ли сообщение думающим
   * @param {string} messageId - ID сообщения
   * @returns {boolean}
   */
  isThinkingMessage(messageId) {
    return this.activeThinkingMessages.has(messageId);
  }

  /**
   * Получает все активные думающие сообщения для чата
   * @param {number} chatId - ID чата
   * @returns {Array} Массив messageId
   */
  getThinkingMessagesForChat(chatId) {
    const messages = [];
    for (const [messageId, data] of this.activeThinkingMessages.entries()) {
      if (data.chatId === chatId) {
        messages.push(messageId);
      }
    }
    return messages;
  }

  /**
   * Очищает все думающие сообщения для чата
   * @param {number} chatId - ID чата
   */
  clearThinkingMessagesForChat(chatId) {
    console.log('🧹 Clearing thinking messages for chat:', chatId);
    const messages = this.getThinkingMessagesForChat(chatId);
    messages.forEach(messageId => this.replaceThinkingMessage(messageId, null));
  }

  /**
   * Синхронизирует состояние менеджера с Redux store
   * Удаляет из менеджера сообщения, которых нет в Redux
   * @param {Array} reduxMessages - массив сообщений из Redux store
   */
  syncWithRedux(reduxMessages) {
    console.log('🔄 Syncing ChatThinkingManager with Redux...');

    // Получаем ID всех thinking сообщений из Redux
    const reduxThinkingIds = new Set(
      reduxMessages.filter(msg => msg.messageData?.isThinking === true).map(msg => msg.messageData.messageId)
    );

    // Удаляем из менеджера сообщения, которых нет в Redux
    const managedIds = Array.from(this.activeThinkingMessages.keys());
    let cleaned = 0;

    managedIds.forEach(messageId => {
      if (!reduxThinkingIds.has(messageId)) {
        console.log('🧹 Cleaning orphaned thinking message from manager:', messageId);
        this.replaceThinkingMessage(messageId, null);
        cleaned++;
      }
    });

    console.log(`✅ Sync complete. Cleaned ${cleaned} orphaned messages.`);
    console.log('🔍 Active thinking messages:', Array.from(this.activeThinkingMessages.keys()));
  }

  /**
   * Очищает все думающие сообщения
   */
  clearAllThinkingMessages() {
    console.log('🧹 Clearing all thinking messages');
    for (const messageId of this.activeThinkingMessages.keys()) {
      this.replaceThinkingMessage(messageId, null);
    }
  }

  /**
   * Запускает интервал очистки устаревших сообщений
   */
  startCleanupInterval() {
    this.cleanupInterval = setInterval(() => {
      const now = Date.now();
      for (const [messageId, data] of this.activeThinkingMessages.entries()) {
        if (now - data.timestamp > this.THINKING_TIMEOUT) {
          console.warn('🕐 Cleaning up expired thinking message:', messageId);
          this.replaceThinkingMessage(messageId, null);
        }
      }
    }, THINKING_CONFIG.CLEANUP_INTERVAL); // Используем конфигурацию
  }

  /**
   * Останавливает интервал очистки
   */
  stopCleanupInterval() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Генерирует уникальный ID для думающего сообщения
   * @param {number} chatId - ID чата
   * @param {string} botName - Имя бота
   * @returns {string}
   */
  static generateThinkingMessageId(chatId, botName = 'bot') {
    return `thinking-${chatId}-${botName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Создает данные для думающего сообщения
   * @param {number} chatId - ID чата
   * @param {string} content - Содержимое сообщения
   * @param {string} botName - Имя бота
   * @returns {Object}
   */
  static createThinkingMessageData(chatId, content = THINKING_CONFIG.MESSAGES.DEFAULT_THINKING, botName = 'Бот') {
    return {
      chatId,
      messageId: ChatThinkingManager.generateThinkingMessageId(chatId, botName),
      content,
      botName,
      timestamp: new Date().toISOString(),
      isThinking: true,
    };
  }

  /**
   * Получает статистику активных думающих сообщений
   * @returns {Object}
   */
  getStatistics() {
    const stats = {
      total: this.activeThinkingMessages.size,
      byChat: {},
      oldest: null,
      newest: null,
    };

    let oldestTime = Infinity;
    let newestTime = 0;

    for (const [messageId, data] of this.activeThinkingMessages.entries()) {
      // By chat
      if (!stats.byChat[data.chatId]) {
        stats.byChat[data.chatId] = 0;
      }
      stats.byChat[data.chatId]++;

      // Timing
      if (data.timestamp < oldestTime) {
        oldestTime = data.timestamp;
        stats.oldest = { messageId, ...data };
      }
      if (data.timestamp > newestTime) {
        newestTime = data.timestamp;
        stats.newest = { messageId, ...data };
      }
    }

    return stats;
  }

  /**
   * Уничтожает менеджер и очищает все ресурсы
   */
  destroy() {
    console.log('💀 Destroying ChatThinkingManager');
    this.stopCleanupInterval();
    this.clearAllThinkingMessages();
  }

  /**
   * Устанавливает кастомный таймаут для думающих сообщений
   * @param {number} timeout - Таймаут в миллисекундах
   */
  setThinkingTimeout(timeout) {
    if (timeout >= THINKING_CONFIG.MIN_TIMEOUT && timeout <= THINKING_CONFIG.MAX_TIMEOUT) {
      this.THINKING_TIMEOUT = timeout;
      console.log('⏰ Thinking timeout updated to:', timeout, 'ms');
    } else {
      console.warn(
        '⚠️ Invalid timeout value. Must be between',
        THINKING_CONFIG.MIN_TIMEOUT,
        'and',
        THINKING_CONFIG.MAX_TIMEOUT
      );
    }
  }

  /**
   * Продлевает время ожидания для конкретного думающего сообщения
   * @param {string} messageId - ID сообщения
   * @param {number} additionalTime - Дополнительное время в миллисекундах
   */
  extendThinkingTime(messageId, additionalTime = THINKING_CONFIG.EXTEND_TIME) {
    const thinking = this.activeThinkingMessages.get(messageId);
    if (thinking) {
      console.log('⏰ Extending thinking time for message:', messageId, 'by', additionalTime, 'ms');

      // Очищаем старые таймеры
      clearTimeout(thinking.timeoutId);
      if (thinking.extendTimeoutId) {
        clearTimeout(thinking.extendTimeoutId);
      }

      // Увеличиваем счетчик продлений
      thinking.extensionCount = (thinking.extensionCount || 0) + 1;

      // Создаем новый таймер с продленным временем
      const timeoutId = setTimeout(() => {
        console.warn('⏰ Extended thinking message timeout:', messageId);
        // Вызываем callback для обработки таймаута (если есть)
        this.handleTimeoutWithExtension(messageId, thinking.chatId, thinking.onTimeout, thinking.onExtend);
      }, additionalTime);

      // Создаем новый таймер для автоматического продления
      const extendTimeoutId = setTimeout(() => {
        console.log('⏰ Auto-extending thinking time for:', messageId);
        this.autoExtendThinkingTime(messageId, thinking.onExtend);
      }, additionalTime - THINKING_CONFIG.AUTO_EXTEND_THRESHOLD);

      // Обновляем данные в Map
      this.activeThinkingMessages.set(messageId, {
        ...thinking,
        timeoutId,
        extendTimeoutId,
        timestamp: Date.now(), // Обновляем timestamp
      });
    }
  }

  /**
   * Автоматически продлевает время ожидания
   * @param {string} messageId - ID сообщения
   * @param {Function} onExtend - Callback для уведомления о продлении
   */
  autoExtendThinkingTime(messageId, onExtend) {
    const thinking = this.activeThinkingMessages.get(messageId);
    if (thinking && thinking.extensionCount < 3) {
      // Максимум 3 автоматических продления
      console.log('🔄 Auto-extending thinking time for:', messageId);

      // Уведомляем о продлении времени
      if (onExtend) {
        onExtend(messageId, thinking.chatId, thinking.extensionCount + 1);
      }

      // Продлеваем время
      this.extendThinkingTime(messageId, THINKING_CONFIG.EXTEND_TIME);
    }
  }

  /**
   * Обрабатывает таймаут с возможностью продления
   * @param {string} messageId - ID сообщения
   * @param {number} chatId - ID чата
   * @param {Function} onTimeout - Callback для таймаута
   * @param {Function} onExtend - Callback для продления
   */
  handleTimeoutWithExtension(messageId, chatId, onTimeout, onExtend) {
    const thinking = this.activeThinkingMessages.get(messageId);
    if (!thinking) return;

    const extensionCount = thinking.extensionCount || 0;

    // Если достигли максимального количества продлений, показываем финальное сообщение об ожидании
    if (extensionCount >= 3) {
      console.warn('⏰ Final timeout for thinking message:', messageId, 'after', extensionCount, 'extensions');
      if (onTimeout) {
        // Показываем сообщение о том, что продолжаем ждать ответ
        onTimeout(messageId, chatId, true); // true указывает, что это финальный таймаут
      }
    } else {
      // Автоматически продлеваем время
      this.autoExtendThinkingTime(messageId, onExtend);
    }
  }
}

// Создаем singleton экземпляр
const chatThinkingManager = new ChatThinkingManager();

export default chatThinkingManager;
export { ChatThinkingManager };
